'use client';

import { useEffect, useState, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountError, useCommerceSource } from '@akademi/site-builder-runtime';
import {
  EMPTY_GUEST_ACCOUNT,
  guestAccountProblem,
  useGuestAccountProblemText,
  useSignUpRefusalText,
  type GuestAccountDraft,
} from './applyAtSignUp';
import { signUp } from './registration';
import {
  EMPTY_REFERENCE_DRAFT,
  EMPTY_TRADE_DRAFT,
  NO_TRADE_REQUIREMENTS,
  tradeApplicationRequest,
  tradeApplicationsOn,
  tradeDraftProblem,
  tradeErrorKind,
  tradeRequirementsOf,
  type TradeApplicationDraft,
  type TradeApplicationStatus,
  type TradeCommerceSource,
  type TradeDocumentType,
  type TradeDocumentUpload,
  type TradeDraftField,
  type TradeDraftProblem,
  type TradeErrorKind,
  type TradeReferenceDraft,
  type TradeRequirement,
  type TradeRequirements,
} from './tradeApplication';

/**
 * Applying for a trade account, as a flow rather than as a layout.
 *
 * <p><strong>Why this is a hook and not a component.</strong> Until 1 October 2026 the whole
 * of it — reading whether the shop takes applications, who is signed in and where their last
 * application stands; the draft, the references and the chosen documents; sending it signed
 * in; sending it WITH a sign-up for a guest and then walking them through the emailed code
 * and the documents that could not ride the sign-up — lived inside
 * {@link ./TradeApplicationForm.TradeApplicationForm}, interleaved with that block's markup.
 * A second storefront theme then needed the same flow under a completely different layout
 * (Allow Energy's numbered Horizon panels, which the shared block's inline styles cannot be
 * made to render), and the only way to get it was to copy two hundred lines of orchestration.
 * Two copies of a flow nobody rehearses end to end diverge silently: the copy that is not the
 * one a bug is found in keeps the bug.
 *
 * <p>So the flow lives here and the markup lives in the blocks. Everything that decides what
 * happens — the phase machine, the after-sign-up machine, validation, the request, the error
 * vocabulary — is in this file and is shared. Everything that decides what it looks like is
 * in the block that calls it.
 *
 * <p>It owns no layout, renders nothing, and reads its words from the platform's own
 * catalogue, so a theme can translate the parts it writes itself and still get the shop's
 * refusals in the shopper's language without re-translating them (see
 * {@link TradeApplicationFlow.problemText}, which takes a theme's own field names).
 */

/** The signed-in shopper an application belongs to, or null for a guest. */
export type TradeViewer = { email: string; name: string } | null;

/** Whether the shop takes applications at all, and — once known — what it asks for. */
export type TradeApplicationPhase =
  | { kind: 'checking' }
  | { kind: 'off' }
  | {
      kind: 'ready';
      viewer: TradeViewer;
      existing: TradeApplicationStatus | null;
      requirements: TradeRequirements;
    };

/**
 * What follows a guest's application, sent with their sign-up: the emailed code when the shop
 * confirms addresses, then the documents the application still lacks, or a sign-in when the
 * account opened without one.
 */
export type TradeAfterSignUp =
  | { kind: 'form' }
  | { kind: 'activate'; email: string; password: string }
  | { kind: 'documents'; status: TradeApplicationStatus | null; email: string }
  | { kind: 'signIn' };

/** One document row as chosen: what it is, and the file once picked. */
export type TradeDocumentDraft = { key: number; type: TradeDocumentType | ''; file: File | null };

/** A refusal to show, and its kind when the shop gave one (`ALREADY_TRADE` earns a link). */
export interface TradeFormError {
  text: string;
  kind?: TradeErrorKind;
}

export interface UseTradeApplicationOptions {
  /**
   * What this block calls each field, for the "these are still needed" sentence. A theme
   * whose labels differ from the platform's passes its own, so the refusal names the labels
   * that are on the screen rather than a second set the applicant has never seen. Omitted,
   * the platform's own names are used.
   */
  fieldLabel?: (field: TradeDraftField) => string;
}

/**
 * Everything a trade application block needs, minus the markup.
 *
 * <p>It takes no links: sign-in, the account page and the terms are drawn by the block,
 * which already has them as props, so passing them here would only be a second place for
 * them to disagree.
 */
export interface TradeApplicationFlow {
  /**
   * The host's commerce source. A host whose source has no `submitTradeApplication` cannot
   * take applications at all, and the block shows its adapter placeholder instead of a form.
   */
  commerce: TradeCommerceSource | null;
  phase: TradeApplicationPhase;
  viewer: TradeViewer;
  /** What the shop asks for; {@link NO_TRADE_REQUIREMENTS} until the read comes back. */
  requirements: TradeRequirements;
  needs(requirement: TradeRequirement): boolean;
  draft: TradeApplicationDraft;
  setDraft: Dispatch<SetStateAction<TradeApplicationDraft>>;
  /** `set('phone')(value)`, as a field's `onChange` wants it. */
  set(field: Exclude<keyof TradeApplicationDraft, 'declared'>): (value: string) => void;
  references: TradeReferenceDraft[];
  setReferences: Dispatch<SetStateAction<TradeReferenceDraft[]>>;
  documents: TradeDocumentDraft[];
  setDocuments: Dispatch<SetStateAction<TradeDocumentDraft[]>>;
  /**
   * The rows that have both a type and a file. These are what ride the trade-application POST
   * as multipart `document_<TYPE>` parts, and what `uploadTradeDocuments` takes afterwards.
   */
  chosen: TradeDocumentUpload[];
  /** The trade-body list's "Other" choice is picked, so a free-text name is asked for. */
  otherBody: boolean;
  setOtherBody: Dispatch<SetStateAction<boolean>>;
  guest: GuestAccountDraft;
  setGuest: Dispatch<SetStateAction<GuestAccountDraft>>;
  busy: boolean;
  error: TradeFormError | null;
  setError: Dispatch<SetStateAction<TradeFormError | null>>;
  /** The application once it is in: what the block says instead of the form. */
  sent: TradeApplicationStatus | null;
  setSent: Dispatch<SetStateAction<TradeApplicationStatus | null>>;
  after: TradeAfterSignUp;
  setAfter: Dispatch<SetStateAction<TradeAfterSignUp>>;
  /** This page, read after mount, for a sign-in link to come back to. */
  here: string | null;
  /** The one submit: signed in it sends the application, a guest's goes with their sign-up. */
  submit(event: FormEvent<HTMLFormElement>): Promise<void>;
  /** Signed in after applying: the documents step, or the application as sent. */
  afterSignedIn(email: string): Promise<void>;
  /** What to show when the application is in but where it stands cannot be read back. */
  pendingStatus(email: string): TradeApplicationStatus;
  /** A field's name in the platform's own words. */
  fieldLabel(field: TradeDraftField): string;
  /**
   * Why the form cannot be sent yet. A theme that names its fields differently from the
   * platform passes its own `fieldLabel`, so the sentence lists the labels on the screen.
   */
  problemText(problem: TradeDraftProblem, fieldLabel?: (field: TradeDraftField) => string): string;
  /** The shop's language, as the request records it. */
  language: string | undefined;
}

export function useTradeApplication(options: UseTradeApplicationOptions = {}): TradeApplicationFlow {
  const commerce = useCommerceSource() as TradeCommerceSource | null;
  const { t, i18n } = useTranslation('site-builder-blocks');

  const [phase, setPhase] = useState<TradeApplicationPhase>({ kind: 'checking' });
  const [draft, setDraft] = useState<TradeApplicationDraft>(EMPTY_TRADE_DRAFT);
  const [references, setReferences] = useState<TradeReferenceDraft[]>([]);
  const [documents, setDocuments] = useState<TradeDocumentDraft[]>([{ key: 0, type: '', file: null }]);
  const [otherBody, setOtherBody] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<TradeFormError | null>(null);
  const [sent, setSent] = useState<TradeApplicationStatus | null>(null);
  const [guest, setGuest] = useState<GuestAccountDraft>(EMPTY_GUEST_ACCOUNT);
  const [after, setAfter] = useState<TradeAfterSignUp>({ kind: 'form' });
  const guestProblemText = useGuestAccountProblemText();
  const signUpRefusalText = useSignUpRefusalText();
  /** This page, read after mount, for the sign-in link to come back to. */
  const [here, setHere] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHere(window.location.pathname + window.location.search);
    if (!commerce?.submitTradeApplication) return;
    void (async () => {
      if (!(await tradeApplicationsOn(commerce))) {
        if (!cancelled) setPhase({ kind: 'off' });
        return;
      }
      const [profile, requirements] = await Promise.all([
        commerce.accountProfile?.().catch(() => null) ?? Promise.resolve(null),
        tradeRequirementsOf(commerce),
      ]);
      let existing: TradeApplicationStatus | null = null;
      if (profile && commerce.myTradeApplication) {
        try {
          existing = await commerce.myTradeApplication();
        } catch (failure) {
          if (tradeErrorKind(failure) === 'NOT_FOUND') {
            if (!cancelled) setPhase({ kind: 'off' });
            return;
          }
          // Where it stands cannot be read just now; the form still works, and the shop answers
          // APPLICATION_OPEN to a second application while one is being reviewed.
        }
      }
      if (cancelled) return;
      const viewer = profile
        ? { email: profile.email, name: [profile.firstName, profile.lastName].filter(Boolean).join(' ') }
        : null;
      if (viewer) {
        setDraft((current) => ({
          ...current,
          email: viewer.email,
          contactName: current.contactName || viewer.name,
        }));
      }
      setReferences(Array.from({ length: requirements.references }, () => ({ ...EMPTY_REFERENCE_DRAFT })));
      setPhase({ kind: 'ready', viewer, existing, requirements });
    })();
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  const viewer = phase.kind === 'ready' ? phase.viewer : null;
  const requirements = phase.kind === 'ready' ? phase.requirements : NO_TRADE_REQUIREMENTS;
  const needs = (requirement: TradeRequirement) => requirements.required.includes(requirement);
  const chosen: TradeDocumentUpload[] = documents
    .filter((row): row is TradeDocumentDraft & { type: TradeDocumentType; file: File } => !!row.type && !!row.file)
    .map((row) => ({ type: row.type, file: row.file }));

  function fieldLabel(field: TradeDraftField): string {
    return t(`commerce.trade.fields.${field}`);
  }

  function problemText(
    problem: TradeDraftProblem,
    names: (field: TradeDraftField) => string = options.fieldLabel ?? fieldLabel,
  ): string {
    switch (problem.kind) {
      case 'INVALID_EMAIL':
        return t('commerce.trade.invalidEmail');
      case 'REFERENCES':
        return t('commerce.trade.problems.references', { count: problem.needed });
      case 'REFERENCE_INCOMPLETE':
        return t('commerce.trade.problems.referenceIncomplete', { number: problem.index + 1 });
      case 'TOO_MANY_FILES':
        return t('commerce.trade.problems.tooManyFiles', { count: problem.max });
      case 'FILE_TOO_LARGE':
        return t('commerce.trade.problems.fileTooLarge', { name: problem.name, size: megabytes(requirements.maxBytes) });
      default:
        return t('commerce.trade.missingFields', {
          fields: problem.fields.map((field) => names(field)).join(', '),
        });
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!viewer) {
      await submitWithSignUp();
      return;
    }
    if (!commerce?.submitTradeApplication || busy || phase.kind !== 'ready') return;
    if (documents.some((row) => row.file && !row.type)) {
      setError({ text: t('commerce.trade.problems.documentType') });
      return;
    }
    const problem = tradeDraftProblem(draft, {
      emailRequired: false,
      requirements,
      references,
      documents: chosen,
    });
    if (problem) {
      setError({ text: problemText(problem) });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setSent(
        await commerce.submitTradeApplication(
          tradeApplicationRequest(draft, { accountEmail: viewer.email, language: i18n?.language, references }),
          chosen,
        ),
      );
    } catch (failure) {
      const kind = tradeErrorKind(failure);
      if (kind === 'NOT_FOUND') setPhase({ kind: 'off' });
      else if (kind === 'INVALID_INPUT' || kind === 'INVALID_FILE') {
        // The shop's own sentence says which field; it is written for the applicant.
        const message = failure instanceof AccountError ? failure.message : '';
        setError({ kind, text: message && message !== kind ? message : t(`commerce.trade.errors.${kind}`) });
      } else setError({ kind, text: t(`commerce.trade.errors.${kind}`) });
    } finally {
      setBusy(false);
    }
  }

  /**
   * A guest's application, in one step with their account (contract §7): the account's fields and
   * the application go to the shop's sign-up together. A refusal is the shop's sentence, and no
   * account is made. Accepted, the shopper is signed in at once, or confirms the emailed code
   * first; then the documents the application still lacks are asked for.
   */
  async function submitWithSignUp() {
    if (!commerce?.register || busy || phase.kind !== 'ready') return;
    const accountProblem = guestAccountProblem(guest);
    if (accountProblem) {
      setError({ text: guestProblemText(accountProblem) });
      return;
    }
    if (documents.some((row) => row.file && !row.type)) {
      setError({ text: t('commerce.trade.problems.documentType') });
      return;
    }
    const problem = tradeDraftProblem(draft, {
      emailRequired: false,
      requirements,
      references,
      documents: chosen,
      documentsLater: true,
    });
    if (problem) {
      setError({ text: problemText(problem) });
      return;
    }
    const email = guest.email.trim().toLowerCase();
    setBusy(true);
    setError(null);
    try {
      const outcome = await signUp(commerce, {
        email,
        firstName: guest.firstName.trim(),
        lastName: guest.lastName.trim(),
        password: guest.password,
        confirmPassword: guest.confirmPassword,
        accountType: 'TRADE',
        application: tradeApplicationRequest(draft, { accountEmail: email, language: i18n?.language, references }),
      });
      if (outcome.signedIn) await afterSignedIn(email);
      else if (outcome.activationRequired) setAfter({ kind: 'activate', email, password: guest.password });
      else setAfter({ kind: 'signIn' });
    } catch (failure) {
      setError({ text: signUpRefusalText(failure) });
    } finally {
      setBusy(false);
    }
  }

  /** Signed in after applying: the documents step when the shop waits for any, or ones were chosen. */
  async function afterSignedIn(email: string) {
    let status: TradeApplicationStatus | null = null;
    try {
      status = (await commerce?.myTradeApplication?.()) ?? null;
    } catch {
      // Where it stands cannot be read just now; the files chosen still go up, and the account
      // page shows anything still outstanding.
    }
    if (commerce?.uploadTradeDocuments && (status?.documentsOutstanding || chosen.length > 0)) {
      setAfter({ kind: 'documents', status, email });
      return;
    }
    setSent(status ?? pendingStatus(email));
  }

  function pendingStatus(email: string): TradeApplicationStatus {
    return {
      status: 'PENDING',
      businessName: draft.businessName.trim(),
      email,
      submittedAt: new Date().toISOString(),
      decidedAt: null,
      reason: null,
    };
  }

  const set = (field: Exclude<keyof TradeApplicationDraft, 'declared'>) => (value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  return {
    commerce,
    phase,
    viewer,
    requirements,
    needs,
    draft,
    setDraft,
    set,
    references,
    setReferences,
    documents,
    setDocuments,
    chosen,
    otherBody,
    setOtherBody,
    guest,
    setGuest,
    busy,
    error,
    setError,
    sent,
    setSent,
    after,
    setAfter,
    here,
    submit,
    afterSignedIn,
    pendingStatus,
    fieldLabel,
    problemText,
    language: i18n?.language,
  };
}

/** Bytes as whole megabytes, as a limit is said: 26 214 400 is "25". */
function megabytes(bytes: number): number {
  return Math.floor(bytes / (1024 * 1024));
}
